import { useEffect, useMemo, useRef, useState } from 'react';
import type { Rol } from '../../api/operaciones';
import type { PermisoCatalogo, PermisoDeRol } from '../../api/ops/equipo';
import { ErrorApi } from '../../api/problema';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { PERMISOS, type ContextoEquipo } from './contexto';
import { TEXTO_CONFLICTO_ROL, textoDeError } from './textos';

/**
 * Roles y permisos (1.11): tarjetas con los roles estándar (no se cambian) y los propios, con cuántas personas tiene cada
 * uno (`member_count`, G139). Un rol se abre en RV Panel con sus permisos agrupados por módulo; los estándar se duplican
 * para crear uno propio; los propios se leen con su versión al abrirlos (`getRole` con `ETag`, G336), se cambian
 * (`updateRole` con `If-Match`; si otra persona lo cambió antes, 412: se vuelve a leer y se avisa) y se archivan si nadie
 * los tiene. Nadie da permisos que no tiene: el panel los enseña desactivados y la API lo comprueba igualmente.
 */
type Abierto = { tipo: 'rol'; rol: Rol } | { tipo: 'nuevo'; base: Rol | null } | null;

export function Roles({ c, crear, onCerrarCrear }: { c: ContextoEquipo; crear: boolean; onCerrarCrear: () => void }) {
    const [abierto, setAbierto] = useState<Abierto>(null);

    // «Crear rol» de la cabecera abre un rol nuevo sin base.
    const visto: Abierto = crear && abierto === null ? { tipo: 'nuevo', base: null } : abierto;

    const cerrar = () => {
        setAbierto(null);
        onCerrarCrear();
    };

    return (
        <>
            <div className="rv-eq-rejilla" role="list" aria-label="Roles">
                {c.roles.map((r) => (
                    <button
                        key={r.id}
                        type="button"
                        role="listitem"
                        className="rv-eq-rol"
                        onClick={() => {
                            setAbierto({ tipo: 'rol', rol: r });
                        }}
                    >
                        <span className="rv-eq-rol__cab">
                            <span className={`rv-eq-rol__baldosa${r.kind === 'custom' ? ' rv-eq-rol__baldosa--propio' : ''}`}>
                                <Icono nombre={r.kind === 'custom' ? 'copy' : 'user-cog'} />
                            </span>
                            <span className={`rv-eq-rol__tipo${r.kind === 'custom' ? ' rv-eq-rol__tipo--propio' : ''}`}>{r.kind === 'custom' ? 'Propio' : 'Estándar'}</span>
                        </span>
                        <strong>{r.name}</strong>
                        <span className="rv-eq-rol__desc">{r.description ?? (r.kind === 'custom' ? 'Rol propio de tu negocio.' : '')}</span>
                        <span className="rv-eq-rol__personas">
                            <Icono nombre="users" />
                            {r.member_count === 1 ? '1 persona' : `${String(r.member_count)} personas`}
                        </span>
                    </button>
                ))}
            </div>
            <PanelRol
                key={visto === null ? 'nada' : visto.tipo === 'rol' ? visto.rol.id : `nuevo-${visto.base?.id ?? ''}`}
                c={c}
                abierto={visto}
                onCerrar={cerrar}
                onDuplicar={(base) => {
                    setAbierto({ tipo: 'nuevo', base });
                    onCerrarCrear();
                }}
                onGuardado={(rol) => {
                    c.refrescar();
                    setAbierto({ tipo: 'rol', rol });
                    onCerrarCrear();
                }}
                onArchivado={() => {
                    c.refrescar();
                    cerrar();
                }}
            />
        </>
    );
}

function PanelRol({
    c,
    abierto,
    onCerrar,
    onDuplicar,
    onGuardado,
    onArchivado,
}: {
    c: ContextoEquipo;
    abierto: Abierto;
    onCerrar: () => void;
    onDuplicar: (base: Rol) => void;
    onGuardado: (rol: Rol) => void;
    onArchivado: () => void;
}) {
    const nuevo = abierto?.tipo === 'nuevo';
    const rol = abierto?.tipo === 'rol' ? abierto.rol : null;
    const base = abierto?.tipo === 'nuevo' ? abierto.base : null;
    const [apartado, setApartado] = useState<'nombre' | 'permisos' | 'miembros'>(nuevo ? 'nombre' : 'permisos');
    const [nombre, setNombre] = useState(base === null ? '' : `${base.name} (copia)`);
    const [elegidos, setElegidos] = useState<PermisoDeRol[]>(rol?.permissions ?? base?.permissions ?? []);
    const [catalogo, setCatalogo] = useState<PermisoCatalogo[] | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [errorNombre, setErrorNombre] = useState<string | null>(null);
    /** Versión (`ETag`) del rol propio leída con getRole; hasta tenerla, no se cambia. */
    const [version, setVersion] = useState<string | null>(null);
    const [lecturas, setLecturas] = useState(0);
    const { ops, negocio, avisar, permisos } = c;
    const gestionar = permisos.puede(PERMISOS.roles) && !c.soloLectura;
    const propio = rol?.kind === 'custom';
    const rolPropio = rol !== null && rol.kind === 'custom' ? rol : null;
    const editable = gestionar && (nuevo || (propio && version !== null));
    const idPropio = gestionar ? (rolPropio?.id ?? null) : null;
    const avisarRef = useRef(avisar);
    useEffect(() => {
        avisarRef.current = avisar;
    }, [avisar]);

    // Un rol propio se lee con su versión antes de cambiarlo (G336); también tras un 412.
    useEffect(() => {
        if (idPropio === null) {
            return;
        }
        let vivo = true;
        ops.getRole(negocio, idPropio)
            .then(({ datos, version: v }) => {
                if (vivo) {
                    setElegidos(datos.permissions);
                    setVersion(v);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    avisarRef.current({ tipo: 'error', texto: textoDeError(e, 'No he podido leer el rol. Prueba de nuevo.') });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, idPropio, lecturas]);

    useEffect(() => {
        if (abierto === null || !permisos.puede(PERMISOS.roles)) {
            return;
        }
        let vivo = true;
        ops.listPermissions(negocio)
            .then((r) => {
                if (vivo) {
                    setCatalogo(r.data);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [abierto, ops, negocio, permisos]);

    const grupos = useMemo(() => {
        const mapa = new Map<string, PermisoCatalogo[]>();
        // Sin el catálogo (quien no gestiona roles), los permisos del rol tal cual.
        const fuente: PermisoCatalogo[] = catalogo ?? elegidos.map((p) => ({ code: p.code, module: '', description: p.code, scope_supported: p.scope }));
        for (const p of fuente) {
            mapa.set(p.module, [...(mapa.get(p.module) ?? []), p]);
        }
        return [...mapa.entries()];
    }, [catalogo, elegidos]);

    if (abierto === null) {
        return (
            <PanelLateral abierto={false} onCerrar={onCerrar} titulo="" movil={c.movil}>
                {null}
            </PanelLateral>
        );
    }

    const tiene = (codigo: string) => elegidos.find((p) => p.code === codigo) ?? null;
    const cambiar = (p: PermisoCatalogo, on: boolean) => {
        setElegidos((actual) => (on ? [...actual.filter((x) => x.code !== p.code), { code: p.code, scope: p.scope_supported === 'own' ? 'own' : 'all' }] : actual.filter((x) => x.code !== p.code)));
    };
    const ambito = (codigo: string, suyo: boolean) => {
        setElegidos((actual) => actual.map((x) => (x.code === codigo ? { ...x, scope: suyo ? 'own' : 'all' } : x)));
    };

    const guardar = () => {
        if (nombre.trim() === '' && nuevo) {
            setApartado('nombre');
            setErrorNombre('Ponle un nombre.');
            return;
        }
        setOcupado(true);
        const datos = { name: nuevo ? nombre.trim() : (rol?.name ?? ''), permissions: elegidos, ...(rol?.description === undefined ? {} : { description: rol.description }) };
        const peticion = nuevo || rol === null ? ops.createRole(negocio, datos) : ops.updateRole(negocio, rol.id, datos, version ?? '');
        peticion
            .then(({ datos: r, version: v }) => {
                avisar({ tipo: 'ok', texto: nuevo ? `Rol «${r.name}» creado.` : 'Cambios guardados.' });
                setVersion(v);
                onGuardado(r);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    avisar({ tipo: 'error', texto: TEXTO_CONFLICTO_ROL });
                    setVersion(null);
                    setLecturas((n) => n + 1);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido guardar el rol. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const archivar = () => {
        if (rol === null) {
            return;
        }
        setOcupado(true);
        ops.archiveRole(negocio, rol.id)
            .then(() => {
                avisar({ tipo: 'ok', texto: `Rol «${rol.name}» archivado.` });
                onArchivado();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido archivar el rol. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const pestanas: ['nombre' | 'permisos' | 'miembros', string][] = nuevo
        ? [
              ['nombre', 'Nombre'],
              ['permisos', 'Permisos'],
          ]
        : [['permisos', 'Permisos']];

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={c.movil}
            tamano="ficha"
            icono={nuevo || propio ? 'copy' : 'user-cog'}
            eyebrow={nuevo ? 'Rol nuevo' : propio ? 'Rol propio' : 'Rol estándar'}
            titulo={nuevo ? 'Rol nuevo' : (rol?.name ?? '')}
            subtitulo={nuevo ? (base === null ? undefined : `Basado en ${base.name}`) : rol?.member_count === 1 ? '1 persona' : `${String(rol?.member_count ?? 0)} personas`}
            accion={editable ? (nuevo ? 'Crear rol' : 'Guardar') : ''}
            ocupado={ocupado}
            onAccion={guardar}
        >
            {pestanas.length > 1 && (
                <div className="rv-eq-botonera" role="tablist" aria-label="Apartados del rol">
                    {pestanas.map(([clave, texto]) => (
                        <button
                            key={clave}
                            type="button"
                            role="tab"
                            aria-selected={apartado === clave}
                            className="rv-eq-botonera__b"
                            onClick={() => {
                                setApartado(clave);
                            }}
                        >
                            <Icono nombre={clave === 'nombre' ? 'pencil' : 'lock'} />
                            <span>{texto}</span>
                        </button>
                    ))}
                </div>
            )}
            {apartado === 'nombre' && (
                <div className="rv-eq-ficha">
                    <h3 className="rv-eq-ceja">Nombre</h3>
                    <Campo
                        etiqueta="Nombre del rol"
                        valor={nombre}
                        onChange={(v) => {
                            setNombre(v);
                            setErrorNombre(null);
                        }}
                        error={errorNombre}
                        ayuda={base === null ? null : `Empieza con los permisos de ${base.name}.`}
                        max={40}
                        nombre="nombre-rol"
                    />
                </div>
            )}
            {apartado === 'permisos' && (
                <div className="rv-eq-ficha">
                    {!nuevo && !propio && gestionar && (
                        <div className="rv-eq-aviso">
                            <span>
                                <Icono nombre="lock" />
                                Los roles estándar no se cambian. Duplícalo para crear uno propio.
                            </span>
                            {rol !== null && (
                                <Boton tipo="secundario" icono="copy" onClick={() => { onDuplicar(rol); }}>
                                    Duplicar
                                </Boton>
                            )}
                        </div>
                    )}
                    {grupos.map(([modulo, lista]) => (
                        <div key={modulo} className="rv-eq-grupo">
                            {modulo !== '' && <h3 className="rv-eq-ceja">{modulo}</h3>}
                            {lista.map((p) => {
                                const suyo = tiene(p.code);
                                // Nadie da permisos que no tiene (la API lo comprueba igualmente).
                                const puedeDar = permisos.puede(p.code);
                                return (
                                    <div key={p.code}>
                                        <Interruptor
                                            etiqueta={p.description}
                                            valor={suyo !== null}
                                            desactivado={!editable || (!puedeDar && suyo === null)}
                                            onChange={(on) => {
                                                cambiar(p, on);
                                            }}
                                        />
                                        {suyo !== null && p.scope_supported === 'own_or_all' && (
                                            <Interruptor
                                                compacto
                                                etiqueta="Solo lo suyo"
                                                valor={suyo.scope === 'own'}
                                                desactivado={!editable}
                                                onChange={(on) => {
                                                    ambito(p.code, on);
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    {rolPropio !== null && gestionar && (
                        <div className="rv-eq-fila-botones">
                            <Boton tipo="secundario" icono="copy" onClick={() => { onDuplicar(rolPropio); }}>
                                Duplicar
                            </Boton>
                            <Boton tipo="secundario" rojo icono="archive" disabled={rolPropio.member_count > 0 || ocupado} onClick={archivar}>
                                Archivar
                            </Boton>
                        </div>
                    )}
                    {rolPropio !== null && gestionar && rolPropio.member_count > 0 && <p className="rv-eq-nota">Este rol lo tiene alguien del equipo: quítaselo antes de archivarlo.</p>}
                </div>
            )}
        </PanelLateral>
    );
}
